import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import { toast } from 'react-toastify';
import AWS from 'aws-sdk';
import { FlagsProvider } from 'flagged';
import Guid from 'guid';
import { debounce } from 'lodash';
import { Confirm, Dimmer, Loader } from 'semantic-ui-react';

import './App.css';

import { clearLocalStorageKeysByPrefix } from '@/common/utils/localStorage/clearLocalStorageKeysByPrefix';
import {
  PATIENT_WORK_QUEUE_FILTER_BUTTONS_STATE,
  PATIENT_WORK_QUEUE_SORTING_FILTERS_STATE,
} from '@/features/PatientWorkQueue/PatientWorkQueueTable.constants';

import featureFlagApi from './api/featureFlags';
import usersApi from './api/users.js';
import CortexPaHeader from './components/CortexPaHeader/CortexPaHeader';
import MainHeader from './components/MainHeader/MainHeader';
import config from './config.js';
import Messages from './containers/Messages';
import { clearAllCustomPopPersistStates } from './containers/PopulationBuilderRR/PopulationColumnSettings';
import { clearAllWqPersistStates } from './containers/Queue/utils';
import SSOHeader from './containers/SsoHeader';
import { UserContext } from './contexts/UserContext.js';
import { initializeAnalytics } from './libs/analytics';
import { getCurrentUser, invokeApig } from './libs/awsLib';
import Cache from './libs/Cache';
import {
  FONT_SIZE_MULTIPLIER_SETTINGS,
  ROLES,
  STORAGE_KEYS,
} from './libs/Constants';
import IdleCheck from './libs/IdleCheck';
import { RealtimeClient } from './libs/RealtimeClient';
import RoleCheck from './libs/RoleCheck';
import Routes from './routes/routes';

export class App extends Component {
  constructor(props) {
    super(props);

    const initialUserState = {
      username: null,
      userTypeId: -1,
      useAutoSave: true,
      fontSizeMultiplier: 0,
      userRoles: [],
      userToken: '',

      setUseAutoSave: (useAutoSave) =>
        this.setState((prev) => ({
          ...prev,
          user: {
            ...prev.user,
            useAutoSave: useAutoSave,
          },
        })),
      setFontSizeMultiplier: (fontSizeMultiplier, save = true) => {
        const MIN_SIZE = FONT_SIZE_MULTIPLIER_SETTINGS.MIN;
        const MAX_SIZE = FONT_SIZE_MULTIPLIER_SETTINGS.MAX;

        if (MIN_SIZE <= fontSizeMultiplier && fontSizeMultiplier <= MAX_SIZE) {
          this.setState((prev) => ({
            ...prev,
            user: {
              ...prev.user,
              fontSizeMultiplier: fontSizeMultiplier,
            },
          }));

          if (save) {
            const fontSizeMultiplierDebouncer = debounce((multiplier) => {
              if (this.state.user.fontSizeMultiplier === multiplier) {
                invokeApig({
                  path: '/users/i/font-size-multiplier',
                  body: { font_size_multiplier: multiplier },
                  method: 'PUT',
                  retry: true,
                }).catch((err) => console.error(err));
                // log error to datadog but silently handle it, this typically
                // happens due to cognito throttling
              }
            }, 1000);
            fontSizeMultiplierDebouncer(fontSizeMultiplier);
          }
        }
      },
    };

    this.state = {
      user: initialUserState,
      userProfile: {},
      isLoadingUserToken: true,
      isAuthenticated: false,
      isAuthenticating: true,
      isDragonflyUser: false,
      iamworking: false,
      alertOpen: false,
      alertMessage: '',
      userGuideUrl: '',
      clientId: this.getClientId(),
      users: [],
      totalPending: null,
      teamPending: [],
      myTeams: [],
      myFacilities: [],
      facilitiesTimezones: [],
      banners: [],
      featureFlags: [],
      colors: [
        'orange',
        'yellow',
        'olive',
        'green',
        'teal',
        'violet',
        'purple',
        'pink',
        'brown',
        'grey',
        'red',
      ],
    };
    this.toggleWorking = this.toggleWorking.bind(this);
    this.turnOffWorker = this.turnOffWorker.bind(this);
    this.handleAlertCancel = this.handleAlertCancel.bind(this);
    this.handleAlertConfirm = this.handleAlertConfirm.bind(this);
    this.handleLogout = this.handleLogout.bind(this);
    this.handleProfile = this.handleProfile.bind(this);
    this.handleBannersAcknowledged = this.handleBannersAcknowledged.bind(this);
    this.cache = new Cache(this.state.user.userToken);

    if (window.DD_LOGS) {
      window.DD_LOGS.addLoggerGlobalContext('version', global.appVersion);
    }
  }

  getClientId = () => 'web-client:' + Guid.raw();

  handleAlertConfirm = () => {
    this.setState({ alertMessage: '', alertOpen: false });
    if (this.props.location.pathname === '/queue') {
      window.location.reload();
    }
  };

  handleBannersAcknowledged = () => {
    if (!this.state.banners || this.state.banners.length === 0) {
      return;
    }

    const ids = this.state.banners.map((banner) => banner.xpas_banner_id);
    invokeApig({
      path: '/banners/mine',
      method: 'POST',
      body: ids,
    });

    this.setState({ banners: [] });
  };

  handleAlertCancel = () =>
    this.setState({ alertMessage: '', alertOpen: false });

  toggleWorking = () => {
    if (!this.state.iamworking) {
      const work = this.iamWorking();
      work.then().then(this.updateWorkers);
      this.setState({ iamworking: true });
    } else {
      const work = this.iamNotWorking();
      work.then().then(this.updateWorkers);
      this.setState({ iamworking: false });
    }
  };

  turnOffWorker = (username) => {
    if (
      !RoleCheck(this.state.userRoles, [
        ROLES.SYSTEM_ADMINISTRATOR,
        ROLES.ADMINISTRATOR,
        ROLES.TEAM_LEAD,
        ROLES.SUPPORT,
      ])
    ) {
      return;
    }

    const newState = {
      workers: this.state.workers.filter((u) => u.username !== username),
    };
    if (username === this.state.user.username) {
      newState.iamworking = false;
    }

    this.setState(newState);

    const work = this.userNotWorking(username);
    work.then(() => {}).then(this.updateWorkers);
  };

  async componentDidUpdate(prevProps) {
    const routeChanged =
      prevProps.location.pathname !== this.props.location.pathname;
    if (this.state.user.username && routeChanged) {
      const bannersResults = await this.getMyBannerMessages();
      this.setState({ banners: bannersResults.banners });
    }
  }

  async componentDidMount() {
    this.setState({ logoutRedirectUrl: '' });
    const currentUser = getCurrentUser();

    if (currentUser === null) {
      this.setState({ isLoadingUserToken: false });
      return;
    }

    const newUserState = { ...this.state.user };

    try {
      const userToken = await this.getUserToken(currentUser);
      newUserState.userToken = userToken;

      const profile = await usersApi.getProfile(userToken);
      if (profile) {
        newUserState.userTypeId = profile.user_type_id;
        newUserState.useAutoSave = profile.use_auto_save;
        newUserState.username = profile.username;
        newUserState.userRoles = profile.user_roles;
        newUserState.isDragonflyUser = profile.isDragonflyUser;
        newUserState.fontSizeMultiplier = profile.font_size_multiplier ?? 0;
        newUserState.applicationUserId = profile.applicationUserId;
        newUserState.userTeams = profile.user_teams;
      }

      this.updateUserToken(userToken);
    } catch (e) {
      console.error(e);
    }

    if (newUserState.userToken !== null) {
      let userStuff = await Promise.all([
        this.amIWorking(),
        this.cache.getUserGuide(),
        this.getMyBannerMessages(),
        featureFlagApi.getFeatureFlags(newUserState.userToken),
      ]);

      this.setState((prev) => ({
        ...prev,
        iamworking: userStuff[0].result,
        userGuideUrl: userStuff[1],
        banners: userStuff[2].banners,
        featureFlags: userStuff[3].featureFlags,
        isDragonflyUser: newUserState.isDragonflyUser,
        userRoles: newUserState.userRoles,
      }));

      this.updateWorkers();

      if (!config.DISABLE_IOT) {
        this.connectRealTimeClient(
          newUserState.userToken,
          newUserState.username
        );
      }

      this.updateTeamStats();
    }
    this.setState((prev) => ({
      ...prev,
      user: newUserState,
      isLoadingUserToken: false,
    }));
  }

  connectRealTimeClient = (userToken, username) => {
    if (this.alertClient) {
      return;
    }

    let clientId = this.state.clientId;

    let messageTopic = username + '/alert';
    this.alertClient = new RealtimeClient(clientId, userToken, [
      messageTopic,
      'review_completed',
    ]);

    this.alertClient.connect().then(() => {
      this.alertClient.onMessageReceived((topic, message) => {
        if (topic.toString().endsWith(messageTopic)) {
          if (message.message) {
            toast(message.message, {
              onClose: function () {
                /*
                  this implementation is to maintain parity with the old alert modal.
                  ideally this will be done in a more react way in DF-24356
                */
                if (window.location.href.indexOf('/queue') > 0) {
                  window.location.reload();
                }
              },
            });
          }
        } else if (topic.toString().endsWith('review_completed')) {
          this.updateTeamStats();
        }
      });
    });
  };

  iamWorking() {
    return invokeApig(
      { service: 'USER', path: `/users/iamworking`, method: 'POST' },
      this.state.user.userToken
    );
  }
  iamNotWorking() {
    return invokeApig(
      { service: 'USER', path: `/users/iamnotworking`, method: 'PUT' },
      this.state.user.userToken
    );
  }
  userNotWorking(username) {
    return invokeApig(
      {
        service: 'USER',
        path: `/users/iamnotworking`,
        method: 'PUT',
        body: { username: username },
      },
      this.state.user.userToken
    );
  }
  amIWorking() {
    return invokeApig(
      { service: 'USER', path: `/users/amiworking`, method: 'GET' },
      this.state.user.userToken
    );
  }
  getWorkers() {
    return invokeApig(
      { service: 'USER', path: `/users/working`, method: 'GET' },
      this.state.user.userToken
    );
  }
  getUserRoles() {
    return invokeApig(
      { service: 'USER', path: `/users/roles`, method: 'GET' },
      this.state.user.userToken
    );
  }
  getMyTeamStats() {
    return invokeApig(
      { service: 'REPORTING', path: `/reporting/my-team-stats`, method: 'GET' },
      this.state.user.userToken
    );
  }

  /**
   * @returns {Promise<{banners: []}>}
   */
  async getMyBannerMessages() {
    if (!this.state.user.userToken) {
      return { banners: [] };
    }

    try {
      return await invokeApig(
        { path: `/banners/mine`, method: 'GET' },
        this.state.user.userToken
      );
    } catch (e) {
      console.error(e);
      return { banners: [] };
    }
  }

  getUserToken(currentUser) {
    return new Promise((resolve, reject) => {
      currentUser.getSession(function (err, session) {
        if (err) {
          reject(err);
          return;
        }
        resolve(session.getIdToken().getJwtToken());
      });
    });
  }

  getAttributes(currentUser) {
    return new Promise((resolve, reject) => {
      currentUser.getUserAttributes(function (err, result) {
        if (err) {
          reject(err);
          return;
        }
        resolve(result);
      });
    });
  }

  updateUserToken = (userToken) => {
    this.setState({
      userToken: userToken,
    });
  };

  updateWorkers = () => {
    const workers = this.getWorkers();
    workers.then((response) => {
      this.setState({ workers: response.users });
    });
  };

  updateTeamStats = () => {
    const myTeamStats = this.getMyTeamStats();
    myTeamStats.then((response) => {
      if (!response || response.length === 0) {
        return;
      }

      this.setState({ totalPending: response[0].pending });
      var found = response.filter((team) => {
        return team.xpas_team_id !== 0;
      });
      if (found.length > 0) {
        this.setState({ teamStats: found });
      }
    });
  };

  userHasAuthenticated = async (authenticated) => {
    this.setState({ isLoadingUserToken: true, isAuthenticated: authenticated });
    const currentUser = getCurrentUser();
    if (currentUser === null) {
      this.setState({ isLoadingUserToken: false });
      return;
    }
    const newUserState = { ...this.state.user };

    try {
      const userToken = await this.getUserToken(currentUser);
      const profile = await usersApi.getProfile(userToken);
      newUserState.userToken = userToken;
      if (profile) {
        newUserState.userTypeId = profile.user_type_id;
        newUserState.useAutoSave = profile.use_auto_save;
        newUserState.username = profile.username;
        newUserState.userRoles = profile.user_roles;
        newUserState.isDragonflyUser = profile.isDragonflyUser;
        newUserState.fontSizeMultiplier = profile.font_size_multiplier ?? 0;
        newUserState.applicationUserId = profile.applicationUserId;
        newUserState.userTeams = profile.user_teams;
      }

      this.setState((prev) => ({
        ...prev,
        isDragonflyUser: profile.isDragonflyUser,
        userRoles: profile.user_roles,
        user: { ...prev.user, ...newUserState },
      }));
      this.updateUserToken(userToken);

      if (newUserState.username && !/:3000/.exec(location.origin)) {
        initializeAnalytics({
          username: newUserState.username,
          applicationUserId: newUserState.applicationUserId,
          roles: newUserState.userRoles?.map((r) => r.role_name),
          isDragonflyUser: newUserState.isDragonflyUser,
          userTypeId: newUserState.userTypeId,
          primaryOrganizationId: profile.main_organization_id,
          primaryOrganizationName: profile.main_organization_name,
        });
      }
    } catch (e) {
      console.error(e);
    }

    if (newUserState.userToken !== null) {
      let userStuff = await Promise.all([
        this.amIWorking(),
        this.cache.getUserGuide(),
        this.getMyBannerMessages(),
        featureFlagApi.getFeatureFlags(newUserState.userToken),
      ]);

      this.setState({
        iamworking: userStuff[0].result,
        userGuideUrl: userStuff[1],
        banners: userStuff[2] ? userStuff[2].banners : [],
        featureFlags: userStuff[3].featureFlags,
        user: newUserState,
      });

      this.updateWorkers();
      this.updateTeamStats();

      if (!config.DISABLE_IOT) {
        this.connectRealTimeClient(
          newUserState.userToken,
          newUserState.username
        );
      }
    }

    this.setState((prev) => ({ ...prev, isLoadingUserToken: false }));
  };

  handleProfile = () => {
    this.props.history.push('/profile');
  };

  handleLogoutCommon = async (options = {}) => {
    const newUserState = { user: {}, userToken: null, username: '' };

    try {
      const currentUser = getCurrentUser();

      if (currentUser !== null) {
        currentUser.signOut();
      }

      if (AWS.config.credentials && AWS.config.credentials.clearCachedId) {
        await AWS.config.credentials.clearCachedId();
      }

      if (this.alertClient) {
        this.alertClient.close();
        this.alertClient = null;
      }

      const username = currentUser ? currentUser.getUsername() : null;
      this.cache.clear(username);

      window.sessionStorage.removeItem(STORAGE_KEYS.lastInvoiceParams);
      window.sessionStorage.removeItem(STORAGE_KEYS.recentlyViewColumns);

      clearAllCustomPopPersistStates();
      clearAllWqPersistStates();
      clearLocalStorageKeysByPrefix(PATIENT_WORK_QUEUE_FILTER_BUTTONS_STATE);
      clearLocalStorageKeysByPrefix(PATIENT_WORK_QUEUE_SORTING_FILTERS_STATE);
    } catch (e) {
      console.error(e);
    }

    this.setState((prev) => ({
      ...prev,
      isAuthenticated: false,
      ...options, // Dynamically merge optional state updates
      user: {
        ...prev.user,
        ...newUserState,
      },
    }));
  };

  handleLogout = () => {
    this.handleLogoutCommon();
  };

  handleLogoutIdle = () => {
    this.handleLogoutCommon({
      alertOpen: false,
      alertMessage: '',
    });
  };

  render() {
    const idleProps = {
      timeout: config.TIMEOUT,
      userToken: this.state.user.userToken,
      handleLogoutIdle: this.handleLogoutIdle,
    };
    const childProps = {
      getCurrentUser: getCurrentUser,
      userProfile: this.state.userProfile,
      userName: this.state.user.username,
      userToken: this.state.user.userToken,
      userTypeId: this.state.user.userTypeId,
      userRoles: this.state.user.userRoles,
      updateUserToken: this.updateUserToken,
      isAuthenticated: this.state.isAuthenticated,
      userHasAuthenticated: this.userHasAuthenticated,
      workers: this.state.workers,
      turnOffWorker: this.turnOffWorker,
      isDragonflyUser: this.state.isDragonflyUser,
      myFacilities: this.state.myFacilities,
      facilitiesTimezones: this.state.facilitiesTimezones,
      updateTeamStats: this.updateTeamStats,
    };

    if (this.state.isLoadingUserToken) {
      return (
        <Dimmer active inverted>
          <Loader>Loading CORTEX.PA</Loader>
        </Dimmer>
      );
    }

    const isOnLoginPage = this.props.location.pathname === '/login';
    const isSsoPage = this.props.location.pathname === '/sso';

    return (
      !this.state.isLoadingUserToken && (
        <UserContext.Provider value={this.state.user}>
          <FlagsProvider features={this.state.featureFlags[0]}>
            <div className='App container'>
              <IdleCheck {...idleProps} />
              {isSsoPage && <SSOHeader />}
              {!isOnLoginPage &&
                !isSsoPage &&
                this.state.userToken &&
                this.state.isDragonflyUser && (
                  <MainHeader
                    username={this.state.user.username}
                    handleProfileClick={this.handleProfile}
                    handleLogoutClick={this.handleLogout}
                    userGuideUrl={this.state.userGuideUrl}
                    handleAutoAssignClick={this.toggleWorking}
                    userRoles={this.state.userRoles}
                    teamStats={this.state.teamStats}
                    totalPending={this.state.totalPending}
                    colors={this.state.colors}
                    iamworking={this.state.iamworking}
                    userToken={this.state.user.userToken}
                  />
                )}
              {!isOnLoginPage && !isSsoPage && !this.state.isDragonflyUser && (
                <CortexPaHeader
                  userToken={this.state.user.userToken}
                  teamStats={this.state.teamStats}
                  totalPending={this.state.totalPending}
                  colors={this.state.colors}
                  userRoles={this.state.userRoles}
                  toggleWorking={this.toggleWorking}
                  iamworking={this.state.iamworking}
                  username={this.state.user.username}
                  handleProfile={this.handleProfile}
                  handleLogout={this.handleLogout}
                  userGuideUrl={this.state.userGuideUrl}
                  appVersion={global.appVersion}
                />
              )}
              <div className={isOnLoginPage ? '' : 'ui fluid main container'}>
                <Messages
                  banners={this.state.banners}
                  handleBannersAcknowledged={this.handleBannersAcknowledged}
                />
                <Confirm
                  open={this.state.alertOpen}
                  content={this.state.alertMessage}
                  onConfirm={this.handleAlertConfirm}
                  onCancel={this.handleAlertCancel}
                  closeOnDimmerClick={true}
                />
                <Routes childProps={childProps} />
              </div>
            </div>
          </FlagsProvider>
        </UserContext.Provider>
      )
    );
  }
}

export default withRouter(App);
